
import React, { useCallback, useEffect, useState } from 'react';
import { cronometrajeAdminService } from '../_services/cronometrajeAdminService';
import './CronometrajeAdmin.css';

const leerObjeto = (valor) => {
  if (!valor) return {};

  if (typeof valor === 'object' && !Array.isArray(valor)) {
    return valor;
  }

  if (typeof valor === 'string') {
    try {
      const resultado = JSON.parse(valor);

      if (
        resultado &&
        typeof resultado === 'object' &&
        !Array.isArray(resultado)
      ) {
        return resultado;
      }
    } catch {
      // Si el JSON no es válido, usamos una configuración vacía.
    }
  }

  return {};
};

const obtenerMensajeError = (error) =>
  error?.response?.data?.error ||
  error?.response?.data?.mensaje ||
  error?.message ||
  'Ocurrió un error inesperado.';

  const formatearFecha = (fecha) => {
    if (!fecha) return 'Sin información';
  
    const fechaValida = new Date(fecha);
  
    if (Number.isNaN(fechaValida.getTime())) {
      return 'Fecha no válida';
    }
  
    return fechaValida.toLocaleString('es-BO', {
      dateStyle: 'medium',
      timeStyle: 'medium',
      timeZone: 'America/La_Paz'
    });
  };

const CronometrajeAdmin = ({ slug }) => {
  const [modo, setModo] = useState('innovatesport');
  const [raceId, setRaceId] = useState('');
  const [configuracionExtra, setConfiguracionExtra] = useState({});
  const [activo, setActivo] = useState(false);
  const [publicado, setPublicado] = useState(false);
  const [accionando, setAccionando] = useState(false);
  const [previewCargando, setPreviewCargando] = useState(false);
  const [previewDatos, setPreviewDatos] = useState(null);
  const [previewError, setPreviewError] = useState('');

  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [existeConfiguracion, setExisteConfiguracion] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  const cargarConfiguracion = useCallback(async () => {
    setCargando(true);
    setError('');
    setMensaje('');

    try {
      const respuesta =
        await cronometrajeAdminService.getConfig(slug);

      if (!respuesta?.ok || !respuesta.configuracion) {
        throw new Error(
          respuesta?.error || 'No se recibió una configuración válida.'
        );
      }

      const datos = respuesta.configuracion;

      setModo(
        datos.modo === 'externo' ? 'externo' : 'innovatesport'
      );
      setRaceId(datos.race_id || '');
      setConfiguracionExtra(
        leerObjeto(datos.configuracion_json ?? datos.configuracion)
      );
      setActivo(Number(datos.activo) === 1 || datos.activo === true);
      setPublicado(
        Number(datos.publicar_resultados) === 1 ||
        datos.publicar_resultados === true
      );

      setExisteConfiguracion(true);
    } catch (err) {
      const status = err?.response?.status;

      if (status === 404) {
        // La competencia todavía no tiene configuración.
        setModo('innovatesport');
        setRaceId('');
        setConfiguracionExtra({});
        setActivo(false);
        setPublicado(false);
        setExisteConfiguracion(false);
      } else {
        setError(obtenerMensajeError(err));
      }
    } finally {
      setCargando(false);
    }
  }, [slug]);

  const cargarVistaPrevia = useCallback(async () => {
    setPreviewCargando(true);
    setPreviewError('');
  
    try {
      const respuesta =
        await cronometrajeAdminService.getLivePreview(slug);
  
      if (!respuesta?.ok) {
        throw new Error(
          respuesta?.error ||
          'No se pudo consultar la instantánea de resultados.'
        );
      }
  
      setPreviewDatos(respuesta);
  
    } catch (err) {
      setPreviewError(obtenerMensajeError(err));
    } finally {
      setPreviewCargando(false);
    }
  }, [slug]);

  useEffect(() => {
    cargarConfiguracion();
  }, [cargarConfiguracion]);

  const handleGuardar = async (event) => {
    event.preventDefault();

    setError('');
    setMensaje('');

    const identificador = raceId.trim();

    if (!/^[a-zA-Z0-9_-]{1,100}$/.test(identificador)) {
      setError(
        'Introduce un identificador válido de Race Result, sin espacios ni caracteres especiales.'
      );
      return;
    }

    setGuardando(true);

    try {
      const respuesta = await cronometrajeAdminService.saveConfig(
        slug,
        {
          modo,
          race_id: identificador,
          configuracion: configuracionExtra,
        }
      );

      if (!respuesta?.ok) {
        throw new Error(
          respuesta?.error || 'No se pudo guardar la configuración.'
        );
      }

      setMensaje(
        respuesta.mensaje ||
          'Configuración guardada. El cronometraje permanece desactivado.'
      );

      // El backend desactiva ambos estados al guardar.
      setActivo(false);
      setPublicado(false);
      setExisteConfiguracion(true);

      // Sincronizamos los datos con lo que realmente guardó el servidor.
      await cargarConfiguracion();
    } catch (err) {
      setError(obtenerMensajeError(err));
    } finally {
      setGuardando(false);
    }
  };
  
const ejecutarAccion = async (accion, operacion) => {
    setError('');
    setMensaje('');
    setAccionando(true);
  
    try {
      const respuesta = await operacion();
  
      if (!respuesta?.ok) {
        throw new Error(
          respuesta?.error ||
          respuesta?.mensaje ||
          'El servidor no pudo completar la operación.'
        );
      }
  
      setMensaje(
        respuesta.mensaje || 'Operación completada correctamente.'
      );
  
      await cargarConfiguracion();
    } catch (err) {
      setError(obtenerMensajeError(err));
    } finally {
      setAccionando(false);
    }
  };
  
  const handleActivar = () => {
    if (
      !window.confirm(
        '¿Activar el cronometraje para esta competencia?'
      )
    ) {
      return;
    }
  
    ejecutarAccion(
      'activar',
      () => cronometrajeAdminService.activate(slug)
    );
  };
  
  const handleDesactivar = () => {
    if (
      !window.confirm(
        '¿Detener el cronometraje y desactivar la publicación de resultados?'
      )
    ) {
      return;
    }
  
    ejecutarAccion(
      'desactivar',
      () => cronometrajeAdminService.deactivate(slug)
    );
  };
  
  const handlePublicacion = () => {
    const nuevoEstado = !publicado;
  
    if (
      !window.confirm(
        nuevoEstado
          ? '¿Publicar los resultados en directo para el público?'
          : '¿Ocultar los resultados en directo al público?'
      )
    ) {
      return;
    }
  
    ejecutarAccion(
      'publicación',
      () =>
        cronometrajeAdminService.setPublication(
          slug,
          nuevoEstado
        )
    );
  };

  const handleFinalizar = () => {
    if (!activo || modo !== 'innovatesport') {
      setError('El cronometraje debe estar activo y en modo InnovateSport.');
      return;
    }
  
    if (
      !previewDatos?.disponible ||
      !previewDatos?.resultados?.length
    ) {
      setError('No hay resultados disponibles para importar.');
      return;
    }
  
    const cantidad = previewDatos.resultados.length;
  
    if (
      !window.confirm(
        `¿Importar ${cantidad} resultados a la tabla oficial de participantes?\n\n` +
        'Esta operación guardará los resultados oficiales y eliminará la instantánea de trabajo.\n\n' +
        'Verifica que el Bridge de Windows esté detenido y que la vista previa contenga los resultados finales.'
      )
    ) {
      return;
    }
  
    ejecutarAccion(
      'importación de resultados',
      async () => {
        const respuesta =
          await cronometrajeAdminService.finalizeLiveResults(slug);
  
        if (!respuesta?.ok) {
          throw new Error(
            respuesta?.error ||
            respuesta?.mensaje ||
            'No se pudieron importar los resultados.'
          );
        }
  
        const guardados =
          respuesta.cantidad ?? respuesta.guardados ?? 0;
  
        setPreviewDatos(null);
  
        return {
          ...respuesta,
          mensaje:
            `Importación oficial completada: ${guardados} resultados guardados en ${respuesta.tabla || 'la tabla de participantes'}.`
        };
      }
    );
  };
  

  if (cargando) {
    return (
      <div className="cronometraje-admin">
        <p>Cargando configuración de cronometraje...</p>
      </div>
    );
  }

  return (
    <div className="cronometraje-admin">
      <div className="cronometraje-admin__header">
        <h2>⏱️ Sistema de cronometraje</h2>
        <p>
          Configura la conexión con Race Result y administra
          los resultados en directo de esta competencia.
        </p>
      </div>

      {error && (
        <div className="cronometraje-admin__alert cronometraje-admin__alert--error">
          {error}
        </div>
      )}

      {mensaje && (
        <div className="cronometraje-admin__alert cronometraje-admin__alert--success">
          {mensaje}
        </div>
      )}

      <section className="cronometraje-admin__card">
        <div className="cronometraje-admin__card-header">
          <div>
            <h3>Configuración de la competencia</h3>
            <p>
              <strong>Slug:</strong> {slug}
            </p>
          </div>

          <span
            className={`cronometraje-admin__status ${
              activo
                ? 'cronometraje-admin__status--active'
                : 'cronometraje-admin__status--inactive'
            }`}
          >
            {activo ? 'Activo' : 'Desactivado'}
          </span>
        </div>

        <p className="cronometraje-admin__publication">
          Publicación de resultados:{' '}
          <strong>{publicado ? 'Activada' : 'Desactivada'}</strong>
        </p>

        {!existeConfiguracion && (
          <p className="cronometraje-admin__notice">
            Esta competencia todavía no tiene una configuración guardada.
            Completa los campos para crearla.
          </p>
        )}

        <form onSubmit={handleGuardar}>
          <div className="cronometraje-admin__field">
            <label htmlFor="cronometraje-modo">
              Modo de cronometraje
            </label>

            <select
              id="cronometraje-modo"
              value={modo}
              onChange={(event) => setModo(event.target.value)}
              disabled={guardando}
            >
              <option value="innovatesport">
                InnovateSport — recepción y publicación desde la plataforma
              </option>
              <option value="externo">
                Externo — resultados gestionados externamente
              </option>
            </select>

            <small>
              El modo define cómo se gestionarán los resultados de esta
              competencia.
            </small>
          </div>

          <div className="cronometraje-admin__field">
            <label htmlFor="cronometraje-race-id">
              Identificador del evento en Race Result
            </label>

            <input
              id="cronometraje-race-id"
              type="text"
              value={raceId}
              onChange={(event) => setRaceId(event.target.value)}
              placeholder="Ej. PBVUO"
              maxLength={100}
              required
              disabled={guardando}
            />

            <small>
              Introduce el identificador del evento, no el slug de la
              competencia.
            </small>
          </div>

          <div className="cronometraje-admin__actions">
            <button
              type="submit"
              disabled={guardando || !raceId.trim()}
              className="cronometraje-admin__button"
            >
              {guardando
                ? 'Guardando...'
                : existeConfiguracion
                  ? 'Guardar configuración'
                  : 'Crear configuración'}
            </button>

            <button
              type="button"
              onClick={cargarConfiguracion}
              disabled={guardando}
              className="cronometraje-admin__button cronometraje-admin__button--secondary"
            >
              Recargar
            </button>
          </div>

          <p className="cronometraje-admin__warning">
            Guardar cambios desactiva el cronometraje y la publicación
            en el servidor. Si la carrera está en curso, no guardes cambios
            hasta que hayas evaluado ese efecto.
          </p>
        </form>
      </section>

      
<section className="cronometraje-admin__card">
  <h3>Control del cronometraje</h3>

  <p>
    Activa o detén la gestión de tiempos y controla su
    publicación pública por separado.
  </p>

  {!existeConfiguracion ? (
    <p className="cronometraje-admin__notice">
      Guarda primero la configuración de esta competencia.
    </p>
  ) : (
    <>
      <div className="cronometraje-admin__actions">
        {!activo ? (
          <button
            type="button"
            onClick={handleActivar}
            disabled={accionando || guardando}
            className="cronometraje-admin__button"
          >
            {accionando ? 'Procesando...' : 'Activar cronometraje'}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleDesactivar}
            disabled={accionando || guardando}
            className="cronometraje-admin__button cronometraje-admin__button--danger"
          >
            {accionando ? 'Procesando...' : 'Detener cronometraje'}
          </button>
        )}

        <button
          type="button"
          onClick={handlePublicacion}
          disabled={
            accionando ||
            guardando ||
            !activo ||
            modo !== 'innovatesport'
          }
          className="cronometraje-admin__button cronometraje-admin__button--secondary"
        >
          {publicado
            ? 'Desactivar publicación'
            : 'Publicar resultados'}
        </button>
      </div>

      {modo !== 'innovatesport' && (
        <p className="cronometraje-admin__notice">
          La publicación desde InnovateSport requiere el modo
          InnovateSport. El modo externo no se publicará desde aquí.
        </p>
      )}

      {!activo && (
        <p className="cronometraje-admin__warning">
          La publicación solo puede activarse cuando el cronometraje
          está activo y el servidor cumple las validaciones necesarias.
        </p>
      )}
    </>
  )}
</section>

<section className="cronometraje-admin__card cronometraje-admin__preview">

  <div className="cronometraje-admin__card-header">
    <div>
      <h3>Vista previa de resultados en vivo</h3>
      <p>
        Consulta los tiempos recibidos desde Race Result
        antes de publicarlos oficialmente.
      </p>
    </div>

    {previewDatos?.disponible && (
      <span className="cronometraje-admin__status cronometraje-admin__status--active">
        Instantánea disponible
      </span>
    )}
  </div>

  <div className="cronometraje-admin__actions">
  <button
    type="button"
    onClick={cargarVistaPrevia}
    disabled={
      previewCargando ||
      modo !== 'innovatesport' ||
      !existeConfiguracion
    }
    className="cronometraje-admin__button"
  >
    {previewCargando
      ? 'Consultando resultados...'
      : previewDatos
        ? 'Actualizar resultados'
        : 'Consultar resultados'}
  </button>

  {activo &&
    modo === 'innovatesport' &&
    previewDatos?.disponible &&
    previewDatos?.resultados?.length > 0 && (
      <button
        type="button"
        onClick={handleFinalizar}
        disabled={accionando || guardando || previewCargando}
        className="cronometraje-admin__button cronometraje-admin__button--danger"
      >
        {accionando
          ? 'Importando resultados...'
          : 'Importar resultados oficiales'}
      </button>
    )}
</div>

  {modo !== 'innovatesport' && (
    <p className="cronometraje-admin__notice">
      La vista previa está disponible únicamente para
      competencias configuradas en modo InnovateSport.
    </p>
  )}

  {!existeConfiguracion && (
    <p className="cronometraje-admin__notice">
      Primero debes guardar la configuración de la competencia.
    </p>
  )}

  {previewError && (
    <div className="cronometraje-admin__alert cronometraje-admin__alert--error">
      {previewError}
    </div>
  )}

  {previewCargando && (
    <p className="cronometraje-admin__preview-loading">
      Consultando la última instantánea recibida...
    </p>
  )}

  {previewDatos && !previewCargando && (
    <>
      <div className="cronometraje-admin__preview-summary">

        <div className="cronometraje-admin__preview-stat">
          <span>Resultados recibidos</span>
          <strong>{previewDatos.cantidad ?? 0}</strong>
        </div>

        <div className="cronometraje-admin__preview-stat">
          <span>Evento Race Result</span>
          <strong>{previewDatos.raceId || 'Sin identificar'}</strong>
        </div>

        <div className="cronometraje-admin__preview-stat">
          <span>Última actualización</span>
          <strong>
            {formatearFecha(previewDatos.actualizadoEn)}
          </strong>
        </div>

      </div>

      {!previewDatos.disponible ||
      !previewDatos.resultados?.length ? (
        <div className="cronometraje-admin__preview-empty">
          Todavía no hay resultados disponibles en la instantánea.
        </div>
      ) : (
        <>
          <div className="cronometraje-admin__preview-table-info">
            Mostrando {previewDatos.resultados.length} resultados.
            Estos datos son una vista previa y no modifican
            los participantes oficiales.
          </div>

          <div className="cronometraje-admin__table-wrapper">
            <table className="cronometraje-admin__table">
              <thead>
                <tr>
                  <th>Posición</th>
                  <th>Dorsal</th>
                  <th>Nombre</th>
                  <th>Distancia</th>
                  <th>Género</th>
                  <th>Categoría</th>
                  <th>Tiempo</th>
                  <th>Estado</th>
                </tr>
              </thead>

              <tbody>
                {previewDatos.resultados.map((resultado, index) => (
                  <tr
                    key={`${resultado.dorsal}-${resultado.distancia}-${index}`}
                  >
                    <td>
                      {resultado.posicion ?? '—'}
                    </td>

                    <td>
                      <strong>{resultado.dorsal || '—'}</strong>
                    </td>

                    <td>
                      {resultado.nombre || 'Sin nombre'}
                    </td>

                    <td>
                      {resultado.distancia || '—'}
                    </td>

                    <td>
                      {resultado.genero || '—'}
                    </td>

                    <td>
                      {resultado.categoria || '—'}
                    </td>

                    <td>
                      <strong>
                        {resultado.tiempo || '—'}
                      </strong>
                    </td>

                    <td>
                      {resultado.estado || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  )}

</section>

    </div>
  );
};

export default CronometrajeAdmin;
